import Dialog from 'core/modules/components/dialogs/components/Dialog';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './ErrorReportModal.scss';

function ErrorReportModal({ open, onClose, onSubmit, exerciseId, activityInstanceId }) {
  const { t } = useTranslation(['chatbot']);
  const [selectedErrorType, setSelectedErrorType] = useState('');
  const [errorDescription, setErrorDescription] = useState('');

  const errorOptions = useMemo(
    () => [
      { id: 'typo', label: t('error_option_typo') },
      { id: 'dont_know', label: t('error_option_dont_know') },
      { id: 'blank', label: t('error_option_blank') },
      { id: 'wrong_solution', label: t('error_option_wrong_solution') },
      { id: 'images_not_loading', label: t('error_option_images_not_loading') },
      { id: 'other', label: t('error_option_other') },
    ],
    [t]
  );

  const handleSubmit = useCallback(() => {
    if (!selectedErrorType) return;

    // Find the selected option to get both ID and label
    const selectedOption = errorOptions.find((option) => option.id === selectedErrorType);

    onSubmit({
      exerciseId,
      activityInstanceId,
      errorType: selectedOption?.label || selectedErrorType, // Send the translated label
      errorTypeId: selectedErrorType, // Keep the ID for reference if needed
      description: errorDescription,
    });

    // Reset form
    setSelectedErrorType('');
    setErrorDescription('');
  }, [selectedErrorType, errorDescription, exerciseId, activityInstanceId, onSubmit, errorOptions]);

  const handleClose = useCallback(() => {
    setSelectedErrorType('');
    setErrorDescription('');
    onClose();
  }, [onClose]);

  return (
    <Dialog
      open={open}
      title={t('error_report_title')}
      size="s"
      buttonTextCancel={t('Cancel')}
      buttonTextSucess={t('Send')}
      onSuccess={handleSubmit}
      onCancel={handleClose}
      disabledSuccess={!selectedErrorType}
      className="error-report-modal"
    >
      <div className="error-report-modal__content">
        <div className="error-report-modal__options">
          {errorOptions.map((option) => (
            <label key={option.id} className="error-report-modal__option">
              <span className="error-report-modal__label">{option.label}</span>
              <input
                type="radio"
                name="errorType"
                value={option.id}
                checked={selectedErrorType === option.id}
                onChange={(e) => setSelectedErrorType(e.target.value)}
                className="error-report-modal__radio"
              />
            </label>
          ))}
        </div>

        <div className="error-report-modal__description">
          <textarea
            value={errorDescription}
            onChange={(e) => setErrorDescription(e.target.value)}
            placeholder={t('error_description_placeholder')}
            className="error-report-modal__textarea"
            rows={3}
          />
        </div>
      </div>
    </Dialog>
  );
}

ErrorReportModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  exerciseId: PropTypes.string,
  activityInstanceId: PropTypes.string,
};

ErrorReportModal.defaultProps = {
  exerciseId: '',
  activityInstanceId: '',
};

export default ErrorReportModal;
